import { useEffect } from "react";
import { Icon } from "@/components/Icon";
import { createFileRoute } from "@tanstack/react-router";

import { Reveal } from "@/components/Reveal";
import { AGENCY } from "@/lib/agency";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/apply-funding")({
  head: () =>
    buildHead({
      title: `Apply for Business Funding - ${AGENCY.name}`,
      description:
        "Apply for business funding online. An advisor will review your application and follow up within one business day.",
      path: "/apply-funding",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Apply", path: "/apply-funding" },
      ],
    }),
  component: ApplyFundingPage,
});

const eyebrow = "text-xs font-semibold uppercase tracking-[0.18em]";

function ApplyFundingPage() {
  useEffect(() => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://link.msgsndr.com/js/form_embed.js"]'
    );
    if (existing) return;
    const script = document.createElement("script");
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 85% 0%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, oklch(0.3 0.1 260) 0%, transparent 50%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -left-16 h-72 w-72 rounded-full bg-accent/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-primary-foreground/10 blur-3xl"
        />

        <div className="relative mx-auto max-w-5xl px-6 py-20 md:py-24 lg:px-10">
          <Reveal className="text-center">
            <p
              className={`${eyebrow} inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-1.5 text-primary-foreground/85`}
            >
              <Icon name="mappin" color="bl" className="h-3.5 w-3.5 text-accent brightness-0 invert"  />
              Serving {AGENCY.area}
            </p>
            <h1 className="mt-6 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
              Apply for Business Funding
              <span className="text-accent"> in minutes.</span>
            </h1>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-xs text-primary-foreground/70">
              <span className="inline-flex items-center gap-1.5">
                <Icon name="sparkles" color="bl" className="h-3.5 w-3.5 text-accent brightness-0 invert"  />
                Secure & confidential
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="checkcircle2" color="bl" className="h-3.5 w-3.5 text-accent brightness-0 invert"  />
                Takes about 3 minutes
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="shieldcheck" color="bl" className="h-3.5 w-3.5 text-accent brightness-0 invert"  />
                Won't impact your credit score
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section
        id="application-form"
        className="relative scroll-mt-20 bg-background"
      >
        <div className="mx-auto w-full max-w-4xl px-6 py-16 md:py-20 lg:px-10">
          <div className="text-center">
            <p className={`${eyebrow} text-accent`}>Application</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
              Fill in your details below
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
              An advisor will review your application and follow up within one
              business day.
            </p>
          </div>

          <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <iframe
              src="https://api.leadconnectorhq.com/widget/form/iILOP7GhpUNskBYRNuWk"
              style={{
                width: "100%",
                height: "876px",
                border: "none",
                borderRadius: "8px",
                display: "block",
              }}
              id="inline-iILOP7GhpUNskBYRNuWk"
              data-layout='{"id":"INLINE"}'
              data-trigger-type="alwaysShow"
              data-activation-type="alwaysActivated"
              data-deactivation-type="neverDeactivate"
              data-form-name="Loan Application"
              data-height="876"
              data-layout-iframe-id="inline-iILOP7GhpUNskBYRNuWk"
              data-form-id="iILOP7GhpUNskBYRNuWk"
              title="Loan Application"
            />
          </div>
        </div>
      </section>
    </>
  );
}
